import React, { useEffect, useState } from "react";
import { AddressForm } from "./AddressForm";
import { AddressCard } from "./AddressCard";
import { addressService, AddressResponse } from "../service/addressService";
import { Button } from "@components/shadcn-ui/button";
import { useToast } from "@customHooks/useToast";
import { Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/shadcn-ui/dialog";
import { useMultiStepForm } from "features/loanApplications/context/MultiStepFormContext";

interface AddressManagerProps {
  applicationId: string;
  leadId: string;
  onAddressesUpdated?: (addressIds: string[]) => void;
}

export const AddressManager: React.FC<AddressManagerProps> = ({
  applicationId,
  leadId,
  onAddressesUpdated,
}) => {
  const [addresses, setAddresses] = useState<AddressResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentEditAddress, setCurrentEditAddress] = useState<AddressResponse | null>(null);
  const { toast } = useToast();
  const { setAddressIds } = useMultiStepForm();

  // Debug log to verify props
  console.log("AddressManager props:", { applicationId, leadId });

  useEffect(() => {
    if (applicationId) {
      fetchAddresses();
    } else {
      setIsLoading(false);
    }
  }, [applicationId]);

  const fetchAddresses = async () => {
    setIsLoading(true);
    try {
      const response = await addressService.getAddressesByApplicationId(applicationId);
      setAddresses(response.addresses || []);
      
      // Update address IDs in context
      const addressIds = response.addresses.map(address => address.id);
      setAddressIds(addressIds);
      
      // Also call the prop callback if provided
      if (onAddressesUpdated) {
        onAddressesUpdated(addressIds);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
      toast({
        variant: "destructive",
        description: "Failed to load addresses",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddressAdded = (newAddress: AddressResponse) => {
    setAddresses(prev => [...prev, newAddress]);
    setShowAddForm(false);
    
    // Update address IDs in context
    const addressIds = [...addresses, newAddress].map(address => address.id);
    setAddressIds(addressIds);
    
    // Also call the prop callback if provided
    if (onAddressesUpdated) {
      onAddressesUpdated(addressIds);
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    if (window.confirm("Are you sure you want to delete this address?")) {
      try {
        await addressService.deleteAddress(addressId);
        
        // Update local state
        const updatedAddresses = addresses.filter(address => address.id !== addressId);
        setAddresses(updatedAddresses);
        
        // Update address IDs in context
        const addressIds = updatedAddresses.map(address => address.id);
        setAddressIds(addressIds);
        
        // Also call the prop callback if provided
        if (onAddressesUpdated) {
          onAddressesUpdated(addressIds);
        }
        
        toast({
          description: "Address deleted successfully",
        });
      } catch (error) {
        console.error("Error deleting address:", error);
        toast({
          variant: "destructive",
          description: "Failed to delete address",
        });
      }
    }
  };

  const handleEditAddress = (address: AddressResponse) => {
    setCurrentEditAddress(address);
    setIsEditDialogOpen(true);
  };

  const handleUpdateAddress = async (updatedAddress: AddressResponse) => {
    if (!currentEditAddress) return;
    
    try {
      // Update local state
      const updatedAddresses = addresses.map(addr => 
        addr.id === updatedAddress.id ? updatedAddress : addr
      );
      setAddresses(updatedAddresses);
      
      // Update address IDs in context
      const addressIds = updatedAddresses.map(address => address.id);
      setAddressIds(addressIds);
      
      // Also call the prop callback if provided
      if (onAddressesUpdated) {
        onAddressesUpdated(addressIds);
      }
      
      setIsEditDialogOpen(false);
      setCurrentEditAddress(null);
    } catch (error) {
      console.error("Error updating address:", error);
      toast({
        variant: "destructive",
        description: "Failed to update address",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading addresses...</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4 mt-4">
        <h2 className="text-lg font-semibold">Address Details</h2>
        <Button 
          onClick={() => setShowAddForm(true)}
          size="sm"
          className="flex items-center gap-1"
        >
          <Plus className="h-4 w-4" /> Add Address
        </Button>
      </div>

      {showAddForm && (
        <AddressForm
          applicationId={applicationId}
          leadId={leadId}
          onAddressAdded={handleAddressAdded}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {addresses.length === 0 && !showAddForm ? (
        <div className="text-center p-8 border border-dashed rounded-md">
          <p className="text-gray-500">No addresses added yet</p>
          <Button 
            onClick={() => setShowAddForm(true)}
            variant="outline"
            className="mt-2"
          >
            Add your first address
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addresses
          .filter((address) => !["CO_APPLICANT_CURRENT", "CO_APPLICANT_PERMANENT","REFERENCE_CURRENT", "REFERENCE_PERMANENT"].includes(address.type))
          .map((address) => (
            <AddressCard
              key={address.id}
              address={address}
              onDelete={handleDeleteAddress}
              onEdit={handleEditAddress}
            />
          ))}
        </div>
      )}

      {/* Edit Address Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden">
          <DialogHeader className="sticky top-0 bg-white z-10 pb-2">
            <DialogTitle>Edit Address</DialogTitle>
          </DialogHeader>
          {currentEditAddress && (
            <AddressForm
              applicationId={applicationId}
              leadId={leadId}
              onAddressAdded={handleUpdateAddress}
              onCancel={() => setIsEditDialogOpen(false)}
              initialData={currentEditAddress}
              isEditMode={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
